import QRCode from 'qrcode';
import { BusinessCard } from '../types';

const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;

class CardServices {
  // Generate QR Code for card
  async generateQRCode(card: BusinessCard): Promise<string> {
    try {
      const cardUrl = `${APP_URL}/card/${card.slug}`;
      const qrDataUrl = await QRCode.toDataURL(cardUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000080',
          light: '#FFFFFF',
        },
      });
      return qrDataUrl;
    } catch (error) {
      console.error('QR generation error:', error);
      throw new Error('Failed to generate QR code');
    }
  }

  // Download QR Code as PNG
  async downloadQRCode(card: BusinessCard): Promise<void> {
    try {
      const qrDataUrl = await this.generateQRCode(card);
      const link = document.createElement('a');
      link.download = `${card.slug}-qr.png`;
      link.href = qrDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('QR download error:', error);
      throw new Error('Failed to download QR code');
    }
  }

  // Generate vCard content
  generateVCard(card: BusinessCard): string {
    const vCard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${card.name}`,
      `ORG:${card.company}`,
      `TITLE:${card.position}`,
      `TEL:${card.phone}`,
      `EMAIL:${card.email}`,
      ...(card.website ? [`URL:${card.website}`] : []),
      ...(card.address ? [`ADR:;;${card.address};;;`] : []),
      ...(card.description ? [`NOTE:${card.description}`] : []),
      ...(card.linkedin ? [`X-SOCIALPROFILE;TYPE=linkedin:${card.linkedin}`] : []),
      ...(card.twitter ? [`X-SOCIALPROFILE;TYPE=twitter:${card.twitter}`] : []),
      ...(card.instagram ? [`X-SOCIALPROFILE;TYPE=instagram:${card.instagram}`] : []),
      'END:VCARD',
    ].join('\r\n');

    return vCard;
  }

  // Download vCard
  downloadVCard(card: BusinessCard): void {
    try {
      const vCardContent = this.generateVCard(card);
      const blob = new Blob([vCardContent], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${card.slug}.vcf`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('vCard download error:', error);
      throw new Error('Failed to download vCard');
    }
  }

  // Share card
  async shareCard(card: BusinessCard): Promise<void> {
    const cardUrl = `${APP_URL}/card/${card.slug}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.name} - ${card.position}`,
          text: `Check out ${card.name}'s digital business card`,
          url: cardUrl,
        });
      } catch (error) {
        console.error('Share error:', error);
        this.copyToClipboard(cardUrl);
      }
    } else {
      this.copyToClipboard(cardUrl);
    }
  }

  // Copy to clipboard
  async copyToClipboard(text: string): Promise<void> {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Copy to clipboard error:', error);
      throw new Error('Failed to copy to clipboard');
    }
  }

  // Generate WhatsApp URL
  generateWhatsAppUrl(card: BusinessCard): string {
    const phone = card.phone.replace(/\D/g, '');
    if (card.whatsappMessage) {
      const message = encodeURIComponent(card.whatsappMessage);
      return `https://wa.me/${phone}?text=${message}`;
    }
    return `https://wa.me/${phone}`;
  }

  // Generate contact actions
  generateContactActions(card: BusinessCard) {
    return {
      phone: `tel:${card.phone}`,
      whatsapp: this.generateWhatsAppUrl(card),
      email: `mailto:${card.email}`,
      website: card.website,
      linkedin: card.linkedin ? `https://linkedin.com/in/${card.linkedin}` : null,
      twitter: card.twitter ? `https://twitter.com/${card.twitter}` : null,
      instagram: card.instagram ? `https://instagram.com/${card.instagram}` : null,
    };
  }

  // Validate card data
  validateCard(card: Partial<BusinessCard>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!card.name?.trim()) {
      errors.push('Name is required');
    }

    if (!card.slug?.trim()) {
      errors.push('Slug is required');
    } else if (!/^[a-z0-9-]+$/.test(card.slug)) {
      errors.push('Slug must contain only lowercase letters, numbers, and hyphens');
    }

    if (!card.position?.trim()) {
      errors.push('Position is required');
    }

    if (!card.phone?.trim()) {
      errors.push('Phone number is required');
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(card.phone)) {
      errors.push('Invalid phone number format');
    }

    if (!card.email?.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(card.email)) {
      errors.push('Invalid email format');
    }

    // WhatsApp message is optional
    // if (!card.whatsappMessage?.trim()) {
    //   errors.push('WhatsApp message is required');
    // }

    if (card.website && !/^https?:\/\/.+/.test(card.website)) {
      errors.push('Website must start with http:// or https://');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Generate slug from name
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  // Format phone number for display
  formatPhone(phone: string): string {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }

  // Get theme configuration
  getThemeConfig() {
    return [
      {
        name: 'modern' as const,
        displayName: 'Modern',
        description: ' ',
        preview: '🎨',
      },
      {
        name: 'classic' as const,
        displayName: 'Classic',
        description: ' ',
        preview: '📋',
      },
      {
        name: 'minimal' as const,
        displayName: 'Minimal',
        description: ' ',
        preview: '⚪',
      },
    ];
  }
}

export const cardServices = new CardServices(); 